import _ from "lodash";
import React, { useState, useRef } from "react";
import classes from "./CollaboratorV2AddEditUserModal.scss";
import { connect } from "react-redux";
import classNames from "classnames";

import { Modal, Button, Checkbox } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { FormTextInput } from "UIComponents/InputTextField";

import {
  createUser,
  updateUser,
  triggerCasExternalInviteEmail,
  portCasExternalDataForProject,
} from "AppComponents/CollaboratorV2/modules/Modules";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  EMAIL_EXIST_WARNING_CODE,
  DISPOSABLE_EMAIL_WARNING_CODE,
  INVALID_EMAIL_WARNING_CODE,
  MULTI_ACCESS_IDENTITY_NOT_FOUND_WARNING_CODE,
} from "AppComponents/CollaboratorV2/modules/constants/stringConstants";
import { inviteProjectGroupSupervisor } from "Projects/routes/Setup/routes/Invite/modules/InviteModules";
import { validateEmail } from "@toddle-edu/common-utils";
import {
  STUDENT,
  TEACHER_USER,
  SHIFT_ENTER_SUBMIT_SHORTCUT,
} from "Constants/stringConstants";
import { SUPERVISOR_APPROVAL_STATUS_APPROVED } from "Projects/Constants/stringConstants";
import { getProjectDetailsFromCache } from "Projects/modules/ProjectGraphqlHelpers";
import { getCurrentProjectGroupDetails } from "Projects/modules/ProjectModules";
import { ENABLE_USER_MIDDLE_NAME } from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getProjectGroupCategoryIdByType } from "Projects/modules/Utils";
import { checkIsProjectCoordinator } from "Projects/config/constants";

const TEST_ID_PREFIX = "projects-collaboratorV2AddEditUserModal";

const CollaboratorV2AddEditUserModal = props => {
  const {
    modalSubHeaderText,
    modalHeaderText,
    modalDoneButtonText,
    onClose,
    mode,
    addUserType = "staff",
    projectGroupId,
    createUser,
    organizationId,
    onAddExternalCollaborator,
    userDetails,
    updateUser,
    extraParams,
    inviteProjectGroupSupervisor,
    showReInviteModal,
    triggerCasExternalInviteEmail,
    userType,
    projectId,
    portCasExternalDataForProject,
    onCloseAddEditModal,
    projectGroupType,
    setToastMsg,
    academicYearId,
    isUserProjectCoordinator,
    projectGroupCategoryType,
  } = props;

  const { t } = useI18n(["common", "project"]);
  const [isLoading, setIsLoading] = useState(false);
  const [reInviteUserModal, setReInviteUserModalDetails] = useState({});
  const [isResendEmailChecked, setIsResendEmailChecked] = useState(false);
  const isEditMode = _.isEqual(mode, "EDIT");

  const emailInputFieldRef = useRef(null);

  const [fieldValues, setFieldValues] = useState(() => {
    switch (mode) {
      case "EDIT":
      case "REINVITE_OLD_DATA": {
        return {
          ...userDetails,
        };
      }
      case "CREATE":
      default: {
        return {
          firstName: "",
          lastName: "",
          email: "",
          organization: "",
          contact: "",
          middleName: "",
        };
      }
    }
  });

  const showResendEmailCheckbox =
    isEditMode &&
    isUserProjectCoordinator &&
    _.isEqual(
      _.get(userDetails, "approvalStatus", ""),
      SUPERVISOR_APPROVAL_STATUS_APPROVED
    );

  const updateFieldDetails = ({ field, value }) => {
    setFieldValues(prevFieldValues => ({
      ...prevFieldValues,
      [field]: value,
    }));
  };

  const updateSupervisorInDropdown = value => {
    if (onAddExternalCollaborator) {
      onAddExternalCollaborator(value);
    }
  };

  const updateReInviteUserModalDetails = details => {
    setReInviteUserModalDetails(details);
  };

  const onCloseReInviteUserModal = () => {
    setReInviteUserModalDetails({});
  };

  const onReInviteClick = async () => {
    setIsLoading(true);
    await inviteProjectGroupSupervisor({
      projectGroupId,
      addedArray: [reInviteUserModal?.userDetails?.id],
      extraParams: {
        queryVariables: {
          getProjectGroupExternalSupervisorsWithProjectsVars:
            extraParams.getProjectGroupExternalSupervisorsWithProjectsVars,
        },
        type: extraParams.type,
      },
    });
    setIsLoading(false);
    onCloseReInviteUserModal();
    onClose();
  };

  const customEmailValidation = () => {
    if (!validateEmail(fieldValues.email)) {
      return t("common:invalid_email_error_msg");
    }
    return false;
  };

  const onDoneClick = async () => {
    const errorCount = emailInputFieldRef.current.isValid();
    if (errorCount > 0) {
      return;
    }
    setIsLoading(true);
    switch (mode) {
      case "EDIT":
        {
          const response = await updateUser({
            userInfo: fieldValues,
          });
          const { isSuccess, warningCode } = response || {};
          if (isSuccess) {
            if (isResendEmailChecked) {
              triggerCasExternalInviteEmail({
                projectGroupId,
                supervisorId: fieldValues.id,
                supervisorEmail: fieldValues.email,
              });
            }
            setIsLoading(false);
            onClose();
          } else {
            switch (warningCode) {
              case EMAIL_EXIST_WARNING_CODE:
                emailInputFieldRef.current.updateErrorState(
                  t("project:external_supervisor_email_exist_error_msg")
                );
                break;
              case DISPOSABLE_EMAIL_WARNING_CODE:
              case INVALID_EMAIL_WARNING_CODE:
                emailInputFieldRef.current.updateErrorState(
                  t("project:external_supervisor_disposable_email_error_msg")
                );
                break;
              case MULTI_ACCESS_IDENTITY_NOT_FOUND_WARNING_CODE:
                emailInputFieldRef.current.updateErrorState(
                  t("project:multi_access_identity_not_found_error_msg")
                );
                break;
              default: {
                setToastMsg("toastMsgs:something_went_wrong");
              }
            }
            setIsLoading(false);
          }
        }
        break;

      case "CREATE":
      default: {
        const response = await createUser({
          userInfo: fieldValues,
          userType: addUserType,
          projectGroupId,
          organizationId,
          extraParams,
        });
        const { isSuccess, warningCode, userDetails = {} } = response || {};
        if (mode !== "REINVITE_OLD_DATA") setIsLoading(false);
        if (isSuccess) {
          // if mode is REINVITE_OLD_DATA, run porting mutation
          if (mode === "REINVITE_OLD_DATA") {
            const projectDetails = getProjectDetailsFromCache({
              projectId,
              projectGroupType,
              academicYearId,
            });
            const supervisorToPort = _.find(
              _.get(projectDetails, "node.fields"),
              { uid: "supervisorEmail" }
            );
            const supervisorToPortEmail = _.trim(
              _.get(supervisorToPort, "value", "")
            );

            // empty emails will not be ported
            if (supervisorToPortEmail) {
              await portCasExternalDataForProject({
                projectGroupId,
                supervisorId: userDetails.id,
                supervisorToPort: supervisorToPort.value,
                projectId,
                projectGroupType,
                projectGroupCategoryType,
              });
            }
            setIsLoading(false);
            onClose();
            if (onCloseAddEditModal) onCloseAddEditModal();
          } else {
            updateSupervisorInDropdown({
              value: userDetails.id,
              label: getFormattedUserName({ user: userDetails }),
              ...userDetails,
            });
            onClose();
          }
        } else {
          setIsLoading(false);
          const projectGroupIds = _.map(
            userDetails?.projectGroups,
            item => item.id
          );

          switch (warningCode) {
            case EMAIL_EXIST_WARNING_CODE:
              {
                if (
                  !showReInviteModal ||
                  (showReInviteModal &&
                    (_.isEmpty(projectGroupIds) ||
                      _.includes(projectGroupIds, projectGroupId)))
                ) {
                  emailInputFieldRef.current.updateErrorState(
                    t("project:external_supervisor_email_exist_error_msg")
                  );
                } else
                  updateReInviteUserModalDetails({
                    displayModal: true,
                    userDetails: {
                      name: `${userDetails?.firstName} ${userDetails?.lastName} `,
                      email: userDetails?.email,
                      id: userDetails?.id,
                      contact: userDetails?.otherDetails?.phoneNumber ?? "-",
                      organizationName:
                        userDetails?.otherDetails?.organizationName ?? "-",
                    },
                  });
              }
              break;
            case DISPOSABLE_EMAIL_WARNING_CODE:
            case INVALID_EMAIL_WARNING_CODE:
              emailInputFieldRef.current.updateErrorState(
                t("project:external_supervisor_disposable_email_error_msg")
              );
              break;
            case MULTI_ACCESS_IDENTITY_NOT_FOUND_WARNING_CODE:
              emailInputFieldRef.current.updateErrorState(
                t("project:multi_access_identity_not_found_error_msg")
              );
              break;
            default: {
              setToastMsg("toastMsgs:something_went_wrong");
            }
          }
        }
      }
    }
  };

  const isDoneDisabled =
    !fieldValues.firstName ||
    !fieldValues.lastName ||
    !fieldValues.email ||
    !fieldValues.organization;

  const customErrorClass = classes.customErrorClass;

  return (
    <>
      <Modal height="606px" width="480px" onClose={onClose} dsVersion="2.0">
        <Modal.Header
          title={
            isEditMode
              ? t("common:edit_with_label", {
                  label: t("common:details"),
                })
              : t(modalHeaderText)
          }
          subtitle={isEditMode ? "" : t(modalSubHeaderText)}
          testId={TEST_ID_PREFIX}
        />

        <Modal.Body isScrollable={true}>
          <div className={classes.bodyContainer}>
            <div>
              <div className={classNames("text-label", classes.label)}>
                {t("common:first_name")}*
              </div>
              <FormTextInput
                size="medium"
                placeholder={t("common:enter_text_here")}
                value={fieldValues.firstName}
                updateInputField={value => {
                  updateFieldDetails({
                    field: "firstName",
                    value: _.trimStart(value),
                  });
                }}
                isRequired={true}
                dsVersion="2.0"
                customErrorClass={customErrorClass}
                testId={`${TEST_ID_PREFIX}-firstName`}
              ></FormTextInput>
            </div>
            {ACLStore.can(ENABLE_USER_MIDDLE_NAME) ? (
              <div>
                <div className={classNames("text-label", classes.label)}>
                  {t("common:middle_name")}
                </div>
                <FormTextInput
                  placeholder={t("common:enter_text_here")}
                  value={fieldValues.middleName}
                  updateInputField={value => {
                    updateFieldDetails({
                      field: "middleName",
                      value: _.trimStart(value),
                    });
                  }}
                  dsVersion="2.0"
                  size="medium"
                  testId={`${TEST_ID_PREFIX}-middleName`}
                ></FormTextInput>
              </div>
            ) : null}
            <div>
              <div className={classNames("text-label", classes.label)}>
                {t("common:last_name")}*
              </div>
              <FormTextInput
                placeholder={t("common:enter_text_here")}
                value={fieldValues.lastName}
                updateInputField={value => {
                  updateFieldDetails({
                    field: "lastName",
                    value: _.trimStart(value),
                  });
                }}
                isRequired={true}
                dsVersion="2.0"
                size="medium"
                customErrorClass={customErrorClass}
                testId={`${TEST_ID_PREFIX}-lastName`}
              ></FormTextInput>
            </div>
            <div>
              <div className={classNames("text-label", classes.label)}>
                {t("common:email")}*
              </div>
              <FormTextInput
                ref={emailInputFieldRef}
                placeholder={t("common:enter_text_here")}
                value={fieldValues.email}
                customValidation={customEmailValidation}
                updateInputField={value => {
                  updateFieldDetails({
                    field: "email",
                    value: _.trimStart(value),
                  });
                }}
                isRequired={true}
                disabled={
                  isEditMode &&
                  _.includes([STUDENT, TEACHER_USER], userType) &&
                  !isUserProjectCoordinator
                }
                dsVersion="2.0"
                size="medium"
                customErrorClass={customErrorClass}
                testId={`${TEST_ID_PREFIX}-email`}
              ></FormTextInput>
            </div>
            <div>
              <div className={classNames("text-label", classes.label)}>
                {t("common:organization")}*
              </div>
              <FormTextInput
                placeholder={t("common:enter_text_here")}
                value={fieldValues.organization}
                updateInputField={value => {
                  updateFieldDetails({
                    field: "organization",
                    value: _.trimStart(value),
                  });
                }}
                isRequired={true}
                dsVersion="2.0"
                size="medium"
                customErrorClass={customErrorClass}
                testId={`${TEST_ID_PREFIX}-organization`}
              ></FormTextInput>
            </div>
            <div>
              <div className={classNames("text-label", classes.label)}>
                {t("common:contact_number")}
              </div>
              <FormTextInput
                placeholder={t("common:enter_text_here")}
                value={fieldValues.contact}
                updateInputField={value => {
                  updateFieldDetails({
                    field: "contact",
                    value: _.trimStart(value),
                  });
                }}
                dsVersion="2.0"
                size="medium"
                testId={`${TEST_ID_PREFIX}-contact`}
              ></FormTextInput>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footerContainer}>
            {showResendEmailCheckbox && (
              <div
                className={classNames(
                  "text-label",
                  classes.resendEmailCheckBox
                )}
              >
                <Checkbox
                  isChecked={isResendEmailChecked}
                  onClick={() => {
                    setIsResendEmailChecked(prevState => !prevState);
                  }}
                  dsVersion="2.0"
                  testId={`${TEST_ID_PREFIX}-resendEmail`}
                />
                <span>{t("project:resend_invite_link")}</span>
              </div>
            )}
            <div className={classes.footerButtonContainer}>
              <Button
                onClick={onClose}
                variant="neutral"
                size="medium"
                type="outlined"
                dsVersion="2.0"
                testId={`${TEST_ID_PREFIX}-cancel`}
              >
                {t("common:cancel")}
              </Button>
              <Button
                disabled={isDoneDisabled}
                onClick={onDoneClick}
                variant="primary"
                type="fill"
                dsVersion="2.0"
                shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                size="medium"
                testId={`${TEST_ID_PREFIX}-save`}
              >
                {!isEditMode ? t(modalDoneButtonText) : t("common:save")}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {isLoading ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
      {reInviteUserModal.displayModal ? (
        <Modal width="480px" height="356px" onClose={onCloseReInviteUserModal}>
          <Modal.Header
            title={t(
              "project:external_supervisor_already_exist_in_other_year_group_title"
            )}
          />
          <Modal.Body>
            <div className={classes.reInviteModalBody}>
              <div>
                {t("common:name_with_label", {
                  label: _.get(reInviteUserModal, "userDetails.name", ""),
                })}
              </div>
              <div>
                {t("common:email_with_label", {
                  label: _.get(reInviteUserModal, "userDetails.email", ""),
                })}
              </div>
              <div>
                {t("common:organization_with_label", {
                  label: _.get(
                    reInviteUserModal,
                    "userDetails.organizationName",
                    ""
                  ),
                })}
              </div>
              <div>
                {t("common:contact_number_with_label", {
                  label: _.get(reInviteUserModal, "userDetails.contact", ""),
                })}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footerButtonContainer}>
              <Button
                variant="neutral"
                size="medium"
                type="outlined"
                dsVersion="2.0"
                onClick={onCloseReInviteUserModal}
                testId={`${TEST_ID_PREFIX}-editDetails`}
              >
                {t("project:edit_details_and_invite")}
              </Button>

              <Button
                variant="primary"
                type="fill"
                dsVersion="2.0"
                shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                size="medium"
                onClick={onReInviteClick}
                testId={`${TEST_ID_PREFIX}-reInvite`}
              >
                {t("project:re_invite_to_this_year_group")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
};

const mapActionCreators = {
  createUser,
  updateUser,
  inviteProjectGroupSupervisor,
  triggerCasExternalInviteEmail,
  portCasExternalDataForProject,
  setToastMsg,
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;
  const {
    type: projectGroupType,
    category: projectGroupCategoryType,
  } = getCurrentProjectGroupDetails({
    state,
  });

  const curriculumProgram = _.get(
    state,
    "platform.currentCurriculumProgram",
    null
  );
  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
  } = curriculumProgram;

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const projectGroupCategoryId = projectGroupCategoryIdByType[projectGroupType];
  const isUserProjectCoordinator = checkIsProjectCoordinator({
    projectGroupCategoryId,
    curriculumProgramId: currentCurriculumProgramId,
  });

  return {
    organizationId,
    userType: userInfo.user_type,
    projectGroupType,
    academicYearId: getSelectedAcademicYearId(state),
    isUserProjectCoordinator,
    projectGroupCategoryType,
  };
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(CollaboratorV2AddEditUserModal);
